import { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Theme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import { makeStyles } from '@mui/styles'
import classnames from 'classnames'
import { MenuItemLink, MenuProps, useSidebarState } from 'react-admin'
import SubMenu from './SubMenu'
import MenuSearch from './MenuSearch'
import Images from '@assets/images'
import { usePermissions, useIsAcAdmin } from '@hooks/useHasPermission'

import { useLocation } from 'react-router-dom'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { BUILDING_INFO_GROUPS, isGroupVisible } from '@/account/buildingInfoGroups'
import { ANALYTICS_CATEGORIES } from '@/analytics/analyticsCategories'
import { getBuildingReports, getPortfolioReports } from '@/analytics/reportRegistry'
import { useEnabledFeatures } from '@hooks/useFeature'

enum MenuNameEnum {
  Account = 'Account',
  Property = 'Property',
  Activate = 'Activate',
  ActivateAdmin = 'ActivateAdmin',
  BuildingInfo = 'BuildingInfo',
  Analytics = 'Analytics',
  PortfolioAnalytics = 'PortfolioAnalytics',
}

// Building dashboards that live outside /analytics but belong to it in the nav.
const ANALYTICS_ESCAPEE_SECTIONS = [
  'workOrderDashboard',
  'workOrderIntelligence',
  'accountingDashboard',
  'collectionDashboard',
  'deficiencyTracker',
  'deficiencyDetail',
  'customFeedbackDetail',
]
// Standalone analytics pages (now redirects into the tab shells).
const ANALYTICS_STANDALONE_SECTIONS = ['alerts', 'reports', 'subscriptions']
// Portfolio dashboards that live outside /portfolioAnalytics but belong to it in the nav.
const PORTFOLIO_ANALYTICS_ESCAPEE_SECTIONS = [
  'workOrderDashboardGlobal',
  'accountingDashboardGlobal',
  'collectionDashboardGlobal',
  'deficiencyTrackerGlobal',
]
// Portfolio Analytics categories in sidebar order (alphabetical, as the
// building Analytics children are after Asset).
const PORTFOLIO_CATEGORY_SLUGS = [
  'asset',
  'equipment',
  'feedback',
  'insurance',
  'invoicing',
  'pmDashboard',
  'propertyCharacteristics',
  'rounds',
  'teamPerformance',
  'tenant',
  'workOrders',
]
const PROPERTY_SECTIONS = [
  'buildingInfo',
  'propertyAdd',
  'marketing',
  'contacts',
  'library',
  'twoQREdit',
  'analytics',
  'asset',
  'coi',
  'calendar',
  'legal',
  'dataArchive',
  'visitorLog',
  'notifications',
  'notificationEngine',
  ...ANALYTICS_ESCAPEE_SECTIONS,
  ...ANALYTICS_STANDALONE_SECTIONS,
]

/**
 * Which nav groups (top level, second level) own a pathname. Drives both the
 * auto-open on navigation and the "you are here" highlight on group headers.
 */
const getRouteSections = (pathname: string): (MenuNameEnum | undefined)[] => {
  const segments = pathname.split('/')
  let routerName = (segments[1] || '').split('?')[0]
  let buildingSection = ''
  if (routerName === 'b' || routerName === 'p') {
    // Building sections live under /b/:bId/<section>, portfolio ones under /p/:gId/<section>.
    routerName = segments[3] || ''
    buildingSection = segments[4] || ''
  }

  let first: MenuNameEnum | undefined
  let second: MenuNameEnum | undefined
  if (['accountCreate', 'properties', 'portfolioTeam', 'pcaHub'].includes(routerName)) {
    first = MenuNameEnum.Account
  } else if (routerName === 'portfolioAnalytics' || PORTFOLIO_ANALYTICS_ESCAPEE_SECTIONS.includes(routerName)) {
    first = MenuNameEnum.Account
    second = MenuNameEnum.PortfolioAnalytics
  } else if (routerName === 'buildingInfo' && buildingSection === 'activate') {
    // Product Add-Ons / Upsells render through the buildingInfo shell but
    // live in the Activate section of the nav.
    first = MenuNameEnum.Activate
  } else if (PROPERTY_SECTIONS.includes(routerName)) {
    first = MenuNameEnum.Property
    if (['buildingInfo', 'propertyAdd'].includes(routerName)) {
      if (buildingSection === 'asset') {
        // Mapping Hub renders through the buildingInfo shell but belongs to
        // the Asset child under Analytics.
        second = MenuNameEnum.Analytics
      } else if (!['coi', 'dataArchive'].includes(buildingSection)) {
        // COI Information and the Terms tab belong to sidebar leaves, which
        // highlight via `selected` — no submenu to open.
        second = MenuNameEnum.BuildingInfo
      }
    } else if (
      routerName === 'analytics' ||
      routerName === 'asset' ||
      ANALYTICS_ESCAPEE_SECTIONS.includes(routerName)
    ) {
      second = MenuNameEnum.Analytics
    }
  } else if (
    [
      'activateReport',
      'collectSurvey',
      'installationRound',
      'digitalProofReport',
      'masterLibrary',
      'scans',
      'feedbackEngine',
      'masterNotepadDashboard',
      'ticketMonitor',
    ].includes(routerName)
  ) {
    first = MenuNameEnum.Activate
  } else if (['admin', 'systemJobs', 'impersonation'].includes(routerName)) {
    first = MenuNameEnum.ActivateAdmin
  }
  return [first, second]
}

const Menu = ({ dense = false }: MenuProps) => {
  const permissions = usePermissions()
  const isAcAdmin = useIsAcAdmin()
  const features = useEnabledFeatures()
  const classes = useStyles()
  const location = useLocation()
  const [open, setSidebarOpen] = useSidebarState()
  const { selectedGroupId, selectedGroupName, selectedPropertyId, selectedPropertyName, user } = useSelector(
    (state) => state.profile
  )
  const memberRole: string = user?.UserContext?.MemberRoleInGroup || ''

  // One open submenu per nesting level: [top group, second level, third level].
  const [openMenus, setOpenMenus] = useState<(MenuNameEnum | undefined)[]>([])
  const handleToggle = (menu: MenuNameEnum, idx: number) => {
    // Collapsed rail: a group icon click expands the sidebar with that group
    // open (never toggles it shut), so the click always reveals something.
    if (!open) {
      setSidebarOpen(true)
      setOpenMenus((prevState) => {
        const next = [...prevState]
        next[idx] = menu
        return next
      })
      return
    }
    setOpenMenus((prevState) => {
      const next = [...prevState]
      next[idx] = prevState[idx] === menu ? undefined : menu
      return next
    })
  }
  const menu = useMemo(() => {
    const {
      ViewAnalytics = false,
      ActivateCustomerFeedback = false,
      GlobalManageCustomers = false,
      PortfolioTeam = false,
      ManageGroups = false,
      ManageEntities = false,
      ManagePropertyInfo = false,
      ManageContacts = false,
      ManageMarketing = false,
      Analytics = false,
      AnalyticsDashboards = false,
      AnalyticsAlerts = false,
      AnalyticsReports = false,
      AnalyticsSubscriptions = false,
      Legal = false,
      ManageDateArchive = false,
      ManageMappingHub = false,
      ManagePcaHub = false,
      InsuranceDashboard = false,
      BuildingPIN = false,
      BuildingTermsAndConditions = false,
      Calendar = false,
      AdminLogin = false,
      GlobalManageRoles = false,
      GlobalManageFeatureFlags = false,
      GlobalManageSystemSettings = false,
    } = permissions || {}
    const portfolioFullAccess =
      PortfolioTeam && ['GMPM', 'OffsitePM', 'ChiefEngineer', 'Admin', 'Manager', 'Executive'].includes(memberRole)
    // The PCA report template is portfolio-level, so its editor sits in the
    // Portfolio group even for a manager who holds no other portfolio access.
    const showPcaHub = ManagePcaHub && !!features.PcaHub
    const selectedEntity = !!selectedPropertyName
    const routerName = location.pathname.split('/')[1]
    // Building sections live under /b/:bId/<section> and portfolio ones under
    // /p/:gId/<section>; highlight by section name.
    const sectionName = routerName === 'b' || routerName === 'p' ? location.pathname.split('/')[3] || '' : routerName
    // Portfolio pages carry the portfolio id the way building pages carry the
    // building's; with none selected the legacy paths reach the shims.
    const portfolioScope = selectedGroupId ? `/p/${selectedGroupId}` : ''
    // No building selected (or a stale name/id desync): fall back to the legacy
    // unscoped paths so links hit the LegacyBuildingRedirect shims instead of
    // producing dead /b//<section> URLs that match no route.
    const scope = selectedPropertyId ? `/b/${selectedPropertyId}` : ''
    /**
     * A building-scoped link with no building to scope it cannot go anywhere
     * useful — the shims send it Home, which reads as the click being ignored.
     * Show it disabled instead, so the reason is on screen. (Reachable only in a
     * name/id desync: the whole group is hidden when no building is selected.)
     */
    const scoped = (pathname: string) => ({ pathname, disabled: !selectedPropertyId })

    const buildingInfoChildren = BUILDING_INFO_GROUPS.filter((group) => !group.hiddenFromNav).map((group) => ({
      ...scoped(`${scope}/buildingInfo/${group.slug}`),
      primaryText: group.label,
      // Same predicate as the in-page tabs: hidden when the user can open
      // none of the group's sections (avoids linking to an empty page).
      show: isGroupVisible(group, permissions, isAcAdmin),
    }))

    // Category links point at the first report the user can open; a category
    // with no permitted reports is hidden. `selected` marks the category
    // whichever of its reports is on screen.
    const reportOptions = getBuildingReports(scope, permissions, features)
    const categoryChild = (slug: string) => {
      const category = ANALYTICS_CATEGORIES.find((entry) => entry.slug === slug)
      const reports = (category?.values || [])
        .map((value) => reportOptions.find((option: any) => option.value === value))
        .filter(Boolean) as { path: string }[]
      return {
        ...scoped(reports[0]?.path || `${scope}/analytics/dashboard`),
        primaryText: category?.label || slug,
        selected: reports.some((option) => location.pathname.startsWith(option.path)),
        show: reports.length > 0,
      }
    }
    // Portfolio Analytics mirrors the building Analytics submenu: one link per
    // category, to its first report this session can open; the category tab
    // strip on the page reaches the rest.
    const portfolioReportOptions = getPortfolioReports(portfolioScope, permissions, features)
    const portfolioAnalyticsChildren = [
      ...PORTFOLIO_CATEGORY_SLUGS.map((slug) => {
        const category = ANALYTICS_CATEGORIES.find((entry) => entry.slug === slug)
        const reports = (category?.portfolioValues || [])
          .map((value) => portfolioReportOptions.find((option) => option.value === value))
          .filter(Boolean) as { path: string }[]
        return {
          pathname: reports[0]?.path || `${portfolioScope}/portfolioAnalytics`,
          primaryText: category?.label || slug,
          selected: reports.some((option) => location.pathname.startsWith(option.path)),
          show: reports.length > 0,
        }
      }),
      {
        pathname: `${portfolioScope}/portfolioAnalytics/subscriptions`,
        primaryText: 'Subscriptions',
      },
    ]

    // The Asset page's tabs in order (MenuTabs' asset list). The link targets
    // the first tab this session can open — the tab routes are feature-guarded,
    // so pointing a Lease-only portfolio at capitalPlanning would land it on
    // Access Denied.
    const assetDashboards = Analytics && AnalyticsDashboards
    const assetPath =
      (assetDashboards && features.CapitalPlanning && `${scope}/asset/capitalPlanning`) ||
      (assetDashboards && features.LeaseIntelligence && `${scope}/asset/lease`) ||
      (assetDashboards && features.RoofIntelligence && `${scope}/asset/roof`) ||
      (assetDashboards && features.ComplianceIntelligence && `${scope}/asset/compliance`) ||
      `${scope}/buildingInfo/asset/mappingHub`
    // Analytics children in spec order (2026-08 nav reorg v2) plus Asset,
    // moved under Analytics per QA feedback. Data Archive, Notifications &
    // Alerts, and Visitor & Security are main-nav items, not children here.
    const analyticsChildren = [
      {
        // One item, five tabs on the page (Capital Planning / Lease / Roof /
        // Compliance / Mapping Hub — MenuTabs' asset list). CAM Reconciliation (spec) has
        // no implementation yet — add as a tab when built.
        ...scoped(assetPath),
        primaryText: 'Asset',
        // Hand-built rather than derived from the registry, so the intelligence
        // modules its four dashboard tabs depend on are checked here too. The
        // Mapping Hub tab is not an intelligence module and keeps the item alive
        // on its own.
        show:
          ((assetDashboards &&
            (features.CapitalPlanning ||
              features.LeaseIntelligence ||
              features.RoofIntelligence ||
              features.ComplianceIntelligence)) ||
            (ManagePropertyInfo && ManageMappingHub)),
        selected: sectionName === 'asset' || location.pathname.includes('/buildingInfo/asset/'),
      },
      categoryChild('equipment'),
      categoryChild('feedback'),
      categoryChild('invoicing'),
      categoryChild('pmDashboard'),
      categoryChild('rounds'),
      categoryChild('suppliers'),
      categoryChild('teamPerformance'),
      categoryChild('tenant'),
      categoryChild('workOrders'),
    ]



    return [
      {
        menuKey: MenuNameEnum.Account,
        sectionLabel: 'Portfolio',
        primaryText: selectedGroupName,
        show: PortfolioTeam || GlobalManageCustomers || showPcaHub,
        leftIcon: <img src={Images.Menu.accountName} alt="logo" />,
        child: [
          {
            pathname: `/accountCreate/${selectedGroupId}`,
            primaryText: 'Account Info',
            leftIcon: <img src={Images.Menu.accountInfo} alt="logo" />,
            show: ManageGroups || portfolioFullAccess,
          },
          {
            pathname: '/properties',
            primaryText: 'Buildings',
            leftIcon: <img src={Images.Menu.properties} alt="logo" />,
            show: ManageEntities || portfolioFullAccess,
          },
          {
            pathname: `${portfolioScope}/portfolioTeam`,
            primaryText: 'Portfolio Team',
            leftIcon: <img src={Images.Menu.contacts} alt="logo" />,
            show: ManageGroups || portfolioFullAccess,
          },
          {
            menuKey: MenuNameEnum.PortfolioAnalytics,
            primaryText: 'Portfolio Analytics',
            leftIcon: <img src={Images.Menu.portfolioAnalytics} alt="logo" />,
            show: ViewAnalytics || portfolioFullAccess,
            child: portfolioAnalyticsChildren,
          },
          {
            pathname: `${portfolioScope}/pcaHub`,
            primaryText: 'PCA Hub',
            leftIcon: <img src={Images.Menu.library} alt="logo" />,
            show: showPcaHub,
          },
        ],
      },
      {
        menuKey: MenuNameEnum.Property,
        sectionLabel: 'Building',
        primaryText: selectedPropertyName,
        show: selectedEntity,
        leftIcon: <img src={Images.Menu.propertyName} alt="logo" />,
        child: [
          // Order per the nav reorg spec §2 — intentional, not alphabetical.
          {
            menuKey: MenuNameEnum.BuildingInfo,
            primaryText: 'Building Info',
            leftIcon: <img src={Images.Menu.propertyInfo} alt="logo" />,
            show: ManagePropertyInfo,
            child: buildingInfoChildren,
          },
          {
            ...scoped(`${scope}/contacts`),
            primaryText: 'Contacts',
            leftIcon: <img src={Images.Menu.contacts} alt="logo" />,
            show: ManageContacts,
          },
          {
            // Re-added after the nav reorg suppressed it (spec §7.1), back in
            // its pre-reorg slot right after Contacts. One item, six tabs on
            // the page (Signs / Rounds / Equipment / Locations / Mechanical
            // Space / PM Library — MenuTabs' library list), and the shell's
            // default-tab redirect lands on the first visible tab.
            //
            // Activate-internal only, and deliberately NOT gated on the
            // Libraries* permissions: customers never see this item however
            // their role is configured, and an AcAdmin always does even where
            // a group's feature flags strip those permissions (MenuTabs'
            // library tabs carry the matching bypass). Nav visibility is not a
            // security boundary — the /library routes stay reachable by URL for
            // anyone holding the underlying permissions.
            ...scoped(`${scope}/library`),
            primaryText: 'QR Library',
            leftIcon: <img src={Images.Menu.library} alt="logo" />,
            selected: [
              'library',
              'twoQREdit',
              'qrcode',
              'roundsEngineBuilder',
              'propertyQrGenerator',
              'customerPortal',
            ].includes(sectionName),
            show: isAcAdmin,
          },
          {
            ...scoped(`${scope}/calendar`),
            primaryText: 'Calendar',
            leftIcon: <img src={Images.icon.calendar} alt="logo" />,
            show: Calendar,
          },
          {
            menuKey: MenuNameEnum.Analytics,
            primaryText: 'Analytics',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
            show: Analytics, //ViewReports,
            child: analyticsChildren,
          },
          {
            // One item, two tabs on the page (Insurance Dashboard / COI
            // Information — MenuTabs' coi list); links straight to the
            // Insurance Dashboard, the spec's landing page.
            ...scoped(`${scope}/coi/insuranceDashboard`),
            primaryText: 'COI',
            leftIcon: <img src={Images.Menu.legal} alt="logo" />,
            show: InsuranceDashboard || (ManagePropertyInfo && BuildingPIN),
            selected: sectionName === 'coi' || location.pathname.includes('/buildingInfo/coi/'),
          },
          {
            ...scoped(`${scope}/visitorLog`),
            primaryText: 'Visitor & Security',
            leftIcon: <img src={Images.icon.visitorLog} alt="logo" />,
            show: true,
          },
          {
            // One item, four tabs on the page (Data Archive / Building Terms &
            // Conditions / Legal / Reports — MenuTabs' dataArchive list); shown
            // to anyone who can open at least one of them, and the shell's
            // default-tab redirect lands on the first visible tab.
            ...scoped(`${scope}/dataArchive`),
            primaryText: 'Data Archive',
            leftIcon: <img src={Images.Menu.legal} alt="logo" />,
            show: ManageDateArchive || (ManagePropertyInfo && BuildingTermsAndConditions) || Legal || AnalyticsReports,
            selected: sectionName === 'dataArchive' || location.pathname.includes('/buildingInfo/dataArchive/'),
          },
          {
            // One item, three tabs on the page (Alerts / Engine /
            // Subscriptions — MenuTabs' notifications list); links straight to
            // Engine, the spec's default landing.
            ...scoped(`${scope}/notifications/engine`),
            primaryText: 'Notifications & Alerts',
            leftIcon: <NotificationsIcon style={{ width: 24, height: 24, color: '#4A3D9E' }} />,
            show: true,
            selected: sectionName === 'notifications',
          },
          {
            // Suppressed (2026-08-22 nav reorg) — routes stay live for re-enable.
            ...scoped(`${scope}/marketing/customerPortal`),
            primaryText: 'Marketing',
            leftIcon: <img src={Images.Menu.marketing} alt="logo" />,
            show: false,
          },
        ],
      },
      {
        menuKey: MenuNameEnum.Activate,
        primaryText: 'Activate',
        show: ActivateCustomerFeedback,
        leftIcon: <img src={Images.Menu.activate} alt="logo" />,
        child: [
          {
            pathname: '/collectSurvey',
            primaryText: 'Survey',
            leftIcon: <img src={Images.Menu.customerSurvey} alt="logo" />,
          },
          {
            pathname: '/intelligencePortal',
            primaryText: 'Intelligence Portal',
            leftIcon: <img src={Images.icon.intelligenceAi} alt="logo" />,
          },
          {
            pathname: '/feedbackEngine',
            primaryText: 'Feedback Engine',
            leftIcon: <img src={Images.Menu.feedbackEngine} alt="logo" />,
          },
          {
            pathname: '/surveyLibrary',
            primaryText: 'Activate Survey',
            leftIcon: <img src={Images.Menu.surveyLibrary} alt="logo" />,
          },
          {
            pathname: '/reportBuilder',
            primaryText: 'Report Builder',
            leftIcon: <img src={Images.Menu.reportBuilder} alt="logo" />,
          },
          {
            pathname: '/ActivateNotepad',
            primaryText: 'Activate Notepad',
            leftIcon: <img src={Images.Menu.activateNotepad} alt="logo" />,
          },
          {
            pathname: '/digitalProofReport',
            primaryText: 'QR Engineering',
            leftIcon: <img src={Images.Menu.digitalProof} alt="logo" />,
          },
          {
            pathname: '/installationRound',
            primaryText: 'Installation',
            leftIcon: <img src={Images.Menu.installation} alt="logo" />,
          },
          {
            pathname: '/activateReport',
            primaryText: 'Customer Feedback',
            leftIcon: <img src={Images.Menu.customerFeedback} alt="logo" />,
          },
          {
            pathname: '/scans/qrLocation',
            primaryText: 'Scans',
            leftIcon: <img src={Images.Menu.scans} alt="logo" />,
          },
          {
            pathname: '/masterLibrary/printLibrary',
            primaryText: 'Master Library',
            leftIcon: <img src={Images.Menu.masterLibrary} alt="logo" />,
          },
          {
            pathname: '/googleRatingReport',
            primaryText: 'Rating Report',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
          },
          {
            pathname: '/promptEngineering',
            primaryText: 'Prompt Engineering',
            leftIcon: <img src={Images.icon.promptEngineering} alt="logo" />,
          },
          {
            pathname: '/lookbook',
            primaryText: 'Lookbook',
            leftIcon: <img src={Images.icon.Lookbook} alt="logo" />,
          },
          {
            pathname: '/customerActivity',
            primaryText: 'Customer Activity Report',
            leftIcon: <img src={Images.Menu.customerActivity} alt="logo" />,
          },
          {
            // Every customer's tickets (specs#3553): Activate staff only.
            pathname: '/ticketMonitor',
            primaryText: 'Ticket Monitor',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
            show: GlobalManageCustomers,
          },
          // Building-scoped tools re-homed here by the 2026-08 nav reorg.
          // The group's ActivateCustomerFeedback gate makes them internal-only;
          // their routes keep their own permission guards.
          {
            ...scoped(`${scope}/analytics/dashboard/roundAnalytics/EquipDetailCollectRounds`),
            primaryText: 'Equipment Collection',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
          },
          {
            ...scoped(`${scope}/masterNotepadDashboard`),
            primaryText: 'Master Notepad',
            leftIcon: <img src={Images.Menu.activateNotepad} alt="logo" />,
          },
          {
            ...scoped(`${scope}/dataArchive/migration`),
            primaryText: 'Migration',
            leftIcon: <img src={Images.Menu.legal} alt="logo" />,
          },
          {
            ...scoped(`${scope}/buildingInfo/activate/productAddOns`),
            primaryText: 'Product Add-Ons',
            leftIcon: <img src={Images.Menu.library} alt="logo" />,
          },
          {
            ...scoped(`${scope}/buildingInfo/activate/upsells`),
            primaryText: 'Upsells',
            leftIcon: <img src={Images.Menu.marketing} alt="logo" />,
          },
        ],
      },
      {
        pathname: '/telemetry',
        primaryText: 'Telemetry',
        show: false,
        leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
      },
      {
        menuKey: MenuNameEnum.ActivateAdmin,
        primaryText: 'Activate Admin',
        show: GlobalManageRoles || GlobalManageFeatureFlags || GlobalManageSystemSettings || GlobalManageCustomers,
        leftIcon: <img src={Images.Menu.activate} alt="logo" />,
        child: [
          {
            pathname: '/admin/roles',
            primaryText: 'Roles',
            leftIcon: <img src={Images.Menu.contacts} alt="logo" />,
            show: GlobalManageRoles,
          },
          {
            pathname: '/admin/featureFlags',
            primaryText: 'Feature Flags',
            leftIcon: <img src={Images.Menu.feedbackEngine} alt="logo" />,
            show: GlobalManageFeatureFlags,
          },
          {
            pathname: '/admin/systemSettings',
            primaryText: 'System Settings',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
            show: GlobalManageSystemSettings,
          },
          {
            pathname: '/systemJobs',
            primaryText: 'System Jobs',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
            show: GlobalManageCustomers,
          },
          {
            pathname: '/impersonation',
            primaryText: 'Impersonate User',
            leftIcon: <img src={Images.Menu.analytics} alt="logo" />,
            show: GlobalManageCustomers,
          },
        ],
      },
    ]
  }, [
    selectedGroupId,
    selectedGroupName,
    selectedPropertyId,
    selectedPropertyName,
    permissions,
    isAcAdmin,
    features,
    memberRole,
    location.pathname,
  ])

  // Re-derive the open menu groups from the URL on every navigation (the old
  // mount-once effect never tracked route changes, so deep links and in-app
  // navigation left the wrong group expanded). Manual toggles persist until a
  // navigation lands in a different group.
  useEffect(() => {
    const [first, second] = getRouteSections(location.pathname)
    setOpenMenus((prev) => {
      const next = [first ?? prev[0], second ?? prev[1]]
      return next.every((menu, idx) => menu === prev[idx]) ? prev : next
    })
  }, [location.pathname])
  // Section the current route lives in — independent of what the user has
  // manually expanded, so the highlight always reflects location.
  const activeMenus = useMemo(() => getRouteSections(location.pathname), [location.pathname])

  const loop = (arr: any[], level: number) => {
    return arr.map((item, idx) => {
      const { pathname, primaryText, leftIcon, show = true, child, disabled, sectionLabel, ...other } = item
      if (!show) return null
      if (child) {
        const { menuKey } = item
        return (
          <div key={menuKey}>
            {/* Collapsed rail shows group icons only — the label needs the
                expanded width, so it rides the open state like the search. */}
            {sectionLabel && open && <div className={classes.sectionLabel}>{sectionLabel}</div>}
            <SubMenu
              handleToggle={() => handleToggle(menuKey, level - 1)}
              // Collapsed rail shows only the group icons; nothing expands
              // in place (leaf/nested items have no icon to stand in for them).
              isOpen={open && menuKey === openMenus[level - 1]}
              active={menuKey === activeMenus[level - 1]}
              name={primaryText}
              icon={leftIcon}
              dense={dense}
              // 32 matches the anchor padding SubMenu's list gives sibling
              // leaf links, so a nested group header (Building Info,
              // Analytics) sits flush with the leaves beside it.
              rootStyle={{ paddingLeft: level > 1 ? 32 : 16 }}
            >
              {loop(child as typeof menu, level + 1)}
            </SubMenu>
            {/* Dividers separate the top-level groups only — nested submenus
                (Building Info, Analytics) keep just their expand arrow. */}
            {level === 1 && idx !== arr.length - 1 && (
              <div style={{ borderBottom: '1px solid #BCBCBC', marginTop: 10 }} />
            )}
          </div>
        )
      }
      const link = (
        <MenuItemLink
          key={`${level}-${idx}-${pathname}`}
          to={{ pathname }}
          state={{ _scrollToTop: true }}
          primaryText={primaryText}
          leftIcon={leftIcon}
          className={classes.menulink}
          dense={dense}
          disabled={disabled}
          {...other}
        />
      )
      // A disabled MenuItem swallows pointer events, so the tooltip needs a
      // wrapper element to hang off.
      return disabled ? (
        <Tooltip key={`${level}-${idx}-${pathname}`} title="Select a building to access this" placement="right">
          <span>{link}</span>
        </Tooltip>
      ) : (
        link
      )
    })
  }

  return (
    <div
      className={classnames(classes.root, {
        [classes.open]: open,
      })}
    >
      {/* Collapsed rail shows group icons only — the search field needs the
          expanded width to be usable, so it rides the open state. */}
      {open && <MenuSearch menu={menu} />}
      {loop(menu, 1)}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 208,
    marginBottom: theme.spacing(1),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '& .RaMenuItemLink-active': {
      backgroundColor: '#F5F5F5',
      borderRadius: 6,
      minHeight: 30,
    },
  },
  open: {
    // width: 349,
  },
  closed: {
    width: 100,
  },
  menuIcon: {
    color: '#211F1F',
    width: 12,
    height: 12,
  },
  sectionLabel: {
    color: '#767676',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '12px 16px 0',
  },
  menulink: {
    color: '#211F1F',
    fontSize: 13,
    minHeight: 30,
    height: 'auto',
    borderRadius: 6,
    fontWeight: 600,
    marginTop: 4,
    '& .RaMenuItemLink-icon': {
      minWidth: 24,
      marginRight: 12,
    },
    // Long labels (Equipment Collection, Customer Activity Report) wrap to a
    // second line ("double-stacked", nav reorg spec) instead of truncating.
    '& .MuiListItemText-primary': {
      whiteSpace: 'normal',
      lineHeight: 1.25,
    },
  },
}))

export default Menu
